import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Flex, Text, Link, Icon
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import RewardBundlingBannerEN from './locales/RewardBundlingBanner_en_US';
import RewardBundlingBannerFR from './locales/RewardBundlingBanner_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(RewardBundlingBannerEN, RewardBundlingBannerFR);

const MAX_CIRCLES = 3;
const CIRCLE_SHOW_THRESHOLD = 250;
const CIRCLE_SIZE = '24px';
const CHECKMARK_SIZE = 12;
const PROMO_GREEN = '#008048';

/**
 * Reward Bundling banner shown at the top of the "100 Points" grid on the BI Hub.
 * Renders the "Get three 100-point rewards for just 250 points" promo, a Terms link
 * that opens the CXS content modal, and (when the user has ≥250 available BI points)
 * a 3-circle progress tracker that fills as distinct 100-pt reward SKUs are added
 * to the ShipToHome basket. When all 3 are collected, an inline "Promo applied 🎉"
 * status message appears beside the circles.
 *
 * Sign-in gate (PRD): the banner is only shown to signed-in Beauty Insider members.
 * Anonymous shoppers do not see it — the promo hinges on BI-points math the guest
 * user has no context for.
 *
 * sthRewardCount is sourced from countDistinctSthRewards (utils/BeautyInsider.js),
 * which already restricts to the ShipToHome basket — BOPIS/SameDay items never
 * contribute, so no per-basket-type guard is needed inside this component.
 *
 * @param {object}   props
 * @param {boolean}  props.isAnonymous - True when the shopper is not signed in; forces the banner to render null.
 * @param {boolean}  props.isEnabled - ConfigHub `loyalty.rewardBundling_{US,CA}.isEnabled` gate.
 * @param {boolean}  props.has100PtRewardsAvailable - True when the biRewardGroups response includes a "100 Points" tier.
 * @param {number}   props.totalAccountPoints - The user's available BI points (basket.availableBiPoints).
 * @param {number}   props.sthRewardCount - Distinct 100-pt SKUs in the STH basket (0..3, capped internally at MAX_CIRCLES).
 * @param {Function} props.onTermsTap - Handler invoked when the inline "Terms apply" link is clicked; opens the CXS Terms modal.
 * @returns {React.ReactNode|null} The banner tree, or null when disabled / anonymous / no 100-pt rewards available.
 */
const RewardBundlingBanner = props => {
    const {
        isAnonymous, isEnabled, has100PtRewardsAvailable, totalAccountPoints, sthRewardCount, onTermsTap, ...rest
    } = props;

    if (isAnonymous || !isEnabled || !has100PtRewardsAvailable) {
        return null;
    }

    const showCircles = (totalAccountPoints || 0) >= CIRCLE_SHOW_THRESHOLD;
    const filledCount = Math.min(sthRewardCount || 0, MAX_CIRCLES);
    const isPromoApplied = filledCount >= MAX_CIRCLES;

    const descriptionParagraph = (
        <Text fontSize='sm'>
            {getText('rewardBundlingDescription')}{' '}
            <Link
                role='link'
                color='blue'
                onClick={onTermsTap}
            >
                {getText('rewardBundlingTermsApply')}
            </Link>
        </Text>
    );

    // Hex values (#F3F1FF background, #18852B filled circle, #CECECE empty circle border)
    // are spec-pinned per STORY.md § Design System Components — do NOT tokenize.
    return (
        <Box {...rest}>
            <Box
                backgroundColor='#F3F1FF'
                padding={4}
                borderRadius='4px'
            >
                <Text
                    is='h3'
                    fontWeight='bold'
                >
                    {getText('rewardBundlingTitle')}
                </Text>
                {!showCircles && descriptionParagraph}
                {showCircles && (
                    <Flex
                        role='progressbar'
                        aria-valuenow={filledCount}
                        aria-valuemin={0}
                        aria-valuemax={MAX_CIRCLES}
                        aria-label={getText('rewardBundlingProgressAccessibility')}
                        marginTop={3}
                    >
                        {Array.from({ length: MAX_CIRCLES }).map((_, index) => {
                            const isFilled = index < filledCount;

                            return (
                                <Flex
                                    key={`reward-bundling-circle-${index}`}
                                    width={CIRCLE_SIZE}
                                    height={CIRCLE_SIZE}
                                    borderRadius='50%'
                                    marginRight={3}
                                    alignItems='center'
                                    justifyContent='center'
                                    backgroundColor={isFilled ? '#18852B' : 'transparent'}
                                    border={isFilled ? 'none' : '1px solid #CECECE'}
                                >
                                    {isFilled && (
                                        <Icon
                                            name='checkmark'
                                            size={CHECKMARK_SIZE}
                                            color='white'
                                        />
                                    )}
                                </Flex>
                            );
                        })}
                        {isPromoApplied && (
                            <Text
                                role='status'
                                aria-live='polite'
                                fontWeight='bold'
                                fontSize='sm'
                                color={PROMO_GREEN}
                                marginLeft={1}
                            >
                                {getText('rewardBundlingPromoApplied')}
                            </Text>
                        )}
                    </Flex>
                )}
            </Box>
            {showCircles && <Box paddingY={2}>{descriptionParagraph}</Box>}
        </Box>
    );
};

export default wrapFunctionalComponent(RewardBundlingBanner, 'RewardBundlingBanner');
